import { useEffect, useRef } from 'react';

import type { ChannelId, ParticipantId } from '../fixtures/domain.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { Marked } from '../components/Marked.js';
import { ChannelPreview } from '../inbox/ChannelPreview.js';
import { StackedPreview } from '../inbox/StackedPreview.js';
import type { InboxRow } from '../inbox/stack.js';
import type { ResultList } from '../inbox/InboxPanel.js';
import type { HitRow } from './search.js';
import styles from './WidgetList.module.css';

/** A live search's three groups; `rows` above are its Chats. */
export interface WidgetListSearch {
  /** The settled term the list was queried for. */
  term: string;
  /** The term the message hits answer, which lags `term` by a round trip. */
  hitTerm: string;
  hitRows: HitRow[];
  candidateRows: InboxRow[];
  /** The hits have not caught up with the box yet, so "no matches" would be premature. */
  pending: boolean;
}

interface WidgetListProps {
  /**
   * The page, with each person's several live applications folded into one row
   * (see `stackRows`). One row per conversation is the ordinary case and still
   * draws a plain `ChannelPreview`.
   */
  rows: InboxRow[];
  loading: boolean;
  /** The read model did not answer. Distinct from "no chats" — see below. */
  error: boolean;
  /** Another page exists; the sentinel below asks for it when it scrolls in. */
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  /** Told which search group the row sat in, for `channel_selected`. */
  onOpen: (channelId: ChannelId, resultList: ResultList) => void;
  /** Which folded rows are open, by person. Held by the panel, not the row. */
  expandedPeople: Set<ParticipantId>;
  onToggleExpand: (id: ParticipantId) => void;
  /** Given while a job scope is in force, which is what makes an empty list
   *  explainable rather than a claim that nobody is waiting. */
  onClearFilter?: () => void;
  search?: WidgetListSearch | null;
}

/**
 * Every chat the employer has with a person, one page of it, newest first —
 * open and ending together, which is the whole list where /chat has two rails.
 *
 * Reuses the page's own `ChannelPreview` rather than a widget-sized variant:
 * the row already earns its height (job tag, reply dot, reward CTA), and a
 * second implementation would be where the widget quietly starts disagreeing
 * with /chat about what a conversation looks like. That is also what makes the
 * merged list legible without a control to split it — the row knows it has
 * ended and says so, in the same terms the Ended rail uses.
 */
export function WidgetList({
  rows,
  loading,
  error,
  hasMore,
  loadingMore,
  loadMore,
  onOpen,
  expandedPeople,
  onToggleExpand,
  onClearFilter,
  search = null,
}: WidgetListProps) {
  const sentinel = useRef<HTMLDivElement>(null);

  // Infinite scroll via an observer rather than a scroll handler, so it costs
  // nothing while the sentinel is off screen — the same way `InboxRows` does it,
  // and for the same reason. Declared before the early returns: a hook cannot
  // sit behind one, and the ref is simply null on the branches without a list.
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMore();
    });
    io.observe(node);
    return () => io.disconnect();
  }, [loadMore, hasMore, rows.length]);

  if (loading) return <p className={styles.hint}>Loading…</p>;

  // Before the empty state, and deliberately: "No chats yet" for a server that
  // never answered tells the employer nobody is waiting on them, which is the
  // one wrong thing this list can say.
  if (error) {
    return (
      <div className={styles.empty}>
        <EmptyState
          icon={<Icon name="chatBubble" size={26} />}
          title="Couldn't load your chats"
          description="Something went wrong on our end. Try opening chat again."
        />
      </div>
    );
  }

  if (rows.length === 0 && !search) {
    return (
      <div className={styles.empty}>
        {/* An empty list under a scope is not an empty inbox, and saying so
            sends an employer looking for chats that are one tap away. */}
        {onClearFilter ? (
          <EmptyState
            icon={<Icon name="filter" size={26} />}
            title="No chats on these jobs"
            description="Widen the job filter to see the rest of your chats."
            action={{ label: 'Clear filter', onClick: onClearFilter }}
          />
        ) : (
          <EmptyState
            icon={<Icon name="chatBubble" size={26} />}
            title="No chats yet"
            description="Applicants you're talking to will show up here."
          />
        )}
      </div>
    );
  }

  const renderRow = (row: InboxRow, list: ResultList) =>
    row.kind === 'stack' ? (
      <StackedPreview
        key={row.key}
        counterpart={row.counterpart}
        items={row.items}
        // The widget opens a thread rather than holding a selection beside
        // one, so no row is ever the "open" one while this list is on screen.
        selectedId={null}
        expanded={expandedPeople.has(row.counterpart.id)}
        onToggleExpand={() => onToggleExpand(row.counterpart.id)}
        onSelect={(item) => onOpen(item.channel.id, list)}
      />
    ) : (
      <ChannelPreview
        key={row.key}
        item={row.item}
        term={search?.term ?? ''}
        active={false}
        onClick={() => onOpen(row.item.channel.id, list)}
      />
    );

  // Same groups, order and verdicts as `/chat`'s list under a search.
  const noMatches =
    search &&
    !search.pending &&
    rows.length === 0 &&
    search.hitRows.length === 0 &&
    search.candidateRows.length === 0;

  return (
    <div className={styles.list}>
      {search && rows.length > 0 && <div className={styles.groupHead}>Chats</div>}
      {rows.map((row) => renderRow(row, 'chats'))}
      {search && search.hitRows.length > 0 && (
        <>
          <div className={styles.groupHead}>Messages</div>
          {search.hitRows.map(({ hit, item, snippet }) => (
            <ChannelPreview
              key={hit.messageId}
              item={item}
              term={search.hitTerm}
              message={{
                preview: <Marked text={snippet} term={search.hitTerm} />,
                at: hit.createdAt,
              }}
              active={false}
              onClick={() => onOpen(item.channel.id, 'messages')}
            />
          ))}
        </>
      )}
      {search && search.candidateRows.length > 0 && (
        <div className={styles.groupHead}>Jobseeker Details</div>
      )}
      {search?.candidateRows.map((row) => renderRow(row, 'jobseeker_details'))}
      {noMatches && (
        <div className={styles.empty}>
          <EmptyState
            title="No matches"
            description={`Nothing matches "${search.term}".`}
            {...(onClearFilter && { action: { label: 'Clear filter', onClick: onClearFilter } })}
          />
        </div>
      )}
      {/* Rendered only while there is a page left to fetch, so reaching the end
          of a complete list observes nothing. */}
      {hasMore && (
        <div ref={sentinel} className={styles.more}>
          {loadingMore ? 'Loading more…' : ''}
        </div>
      )}
    </div>
  );
}
